import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Trash2, ShoppingBag, MessageCircle } from 'lucide-react';
import { useWishlist } from '../../context/WishlistContext';

const WishlistDrawer = ({ onSelectArtwork }) => {
  const { wishlist, isWishlistOpen, closeWishlist, removeFromWishlist, clearWishlist } = useWishlist();

  const handleWhatsAppInquiry = () => {
    const items = wishlist.map(item => `- ${item.title} (${item.price})`).join('\n');
    const message = encodeURIComponent(
      `Hi! I'm interested in the following artworks:\n\n${items}\n\nPlease share more details.`
    );
    window.open(`https://wa.me/919381690689?text=${message}`, '_blank');
  };

  return (
    <AnimatePresence>
      {isWishlistOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[1001]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeWishlist}
          />

          {/* Drawer */}
          <motion.div
            className="fixed right-0 top-0 h-full w-full max-w-md bg-gradient-to-b from-[#111111] to-[#0a0a0a] border-l border-[#1a1a1a] z-[1002] flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            data-testid="wishlist-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#1a1a1a]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Heart className="text-[#D4AF37]" size={20} />
                </div>
                <div>
                  <h2 className="font-cinzel text-xl text-white">My Wishlist</h2>
                  <p className="font-manrope text-sm text-gray-500">{wishlist.length} items saved</p>
                </div>
              </div>
              <button
                onClick={closeWishlist}
                className="w-10 h-10 rounded-full bg-[#1a1a1a] text-gray-400 hover:text-white hover:bg-[#2a2a2a] transition-all flex items-center justify-center"
                data-testid="close-wishlist-btn"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {wishlist.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-20 h-20 rounded-full bg-[#1a1a1a] flex items-center justify-center mb-4">
                    <Heart className="text-gray-600" size={32} />
                  </div>
                  <h3 className="font-cinzel text-lg text-white mb-2">Your wishlist is empty</h3>
                  <p className="font-manrope text-sm text-gray-500 max-w-xs">
                    Save your favorite artworks by clicking the heart icon on any piece you love.
                  </p>
                  <button
                    onClick={closeWishlist}
                    className="mt-6 px-6 py-2.5 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-manrope text-sm hover:bg-[#D4AF37]/20 transition-all"
                  >
                    Browse Collection
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {wishlist.map((artwork, index) => (
                    <motion.div
                      key={artwork.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 100 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl overflow-hidden group hover:border-[#D4AF37]/30 transition-all"
                    >
                      <div className="flex gap-4 p-3">
                        {/* Image */}
                        <div 
                          className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                          onClick={() => {
                            closeWishlist();
                            onSelectArtwork(artwork);
                          }}
                        >
                          <img
                            src={artwork.images?.[0] || artwork.image}
                            alt={artwork.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h4 
                            className="font-cinzel text-sm text-white mb-1 truncate cursor-pointer hover:text-[#D4AF37] transition-colors"
                            onClick={() => {
                              closeWishlist();
                              onSelectArtwork(artwork);
                            }}
                          >
                            {artwork.title}
                          </h4>
                          <p className="font-manrope text-xs text-gray-500 mb-2">{artwork.size}</p>
                          <p className="font-cinzel text-lg text-[#D4AF37] font-semibold">{artwork.price}</p>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => removeFromWishlist(artwork.id)}
                          className="w-8 h-8 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all flex items-center justify-center flex-shrink-0 self-start"
                          data-testid={`remove-wishlist-${artwork.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            {wishlist.length > 0 && (
              <div className="p-5 border-t border-[#1a1a1a] space-y-3">
                {/* Inquire All Button */}
                <button
                  onClick={handleWhatsAppInquiry}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#25D366] text-white font-manrope font-semibold text-base hover:bg-[#22c55e] transition-all"
                  data-testid="inquire-all-btn"
                >
                  <MessageCircle size={20} />
                  Inquire About All ({wishlist.length})
                </button>

                {/* Clear Wishlist */}
                <button
                  onClick={clearWishlist}
                  className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-gray-400 hover:text-red-400 font-manrope text-sm transition-all"
                  data-testid="clear-wishlist-btn"
                >
                  <Trash2 size={16} />
                  Clear Wishlist
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WishlistDrawer;
